import { Link } from "react-router-dom";
import { ALL_CALCULATORS, CalculatorItem } from "@/components/CalculatorSearchDialog";
import { ChevronRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

interface RelatedCalculatorsProps {
  currentId: string;
  maxItems?: number;
}

export function RelatedCalculators({ currentId, maxItems = 4 }: RelatedCalculatorsProps) {
  // Find current calculator to prioritize same category, then others
  const current = ALL_CALCULATORS.find((c) => c.id === currentId);
  const others = ALL_CALCULATORS.filter((c) => c.id !== currentId);

  let sorted: CalculatorItem[] = [];
  if (current) {
    const sameCategory = others.filter((c) => c.category === current.category);
    const differentCategory = others.filter((c) => c.category !== current.category);
    sorted = [...sameCategory, ...differentCategory].slice(0, maxItems);
  } else {
    sorted = others.slice(0, maxItems);
  }

  return (
    <section className="my-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Explore More Calculators
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Quickly calculate investments, taxes, fuel efficiency, and health metrics
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {sorted.map((item, idx) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Link
                to={item.url}
                className="group flex flex-col justify-between h-full p-4 rounded-2xl border border-border/70 bg-card hover:bg-muted/40 hover:border-primary/40 transition-all duration-200 shadow-sm hover:shadow"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-sm">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 bg-muted px-2 py-0.5 rounded-md">
                      {item.category}
                    </span>
                  </div>
                  <h4 className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-snug">
                    {item.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-border/40 flex items-center justify-between text-xs font-semibold text-primary">
                  <span>Calculate Now</span>
                  <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
